import PriceDisplay from '@/components/PriceDisplay';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO, BookedTripDTO } from '@/types/dto/agent';
import { isAdmissionInactive } from '@/utils/admission';
import { Icons, Typography } from '@packages/webshop-design-system';
import type { FC } from 'react';

interface CurrentTicketSectionProps {
  trip: BookedTripDTO | null;
  legIndex: number;
}

const CurrentTicketSection: FC<CurrentTicketSectionProps> = ({
  trip,
  legIndex,
}) => {
  const admissions = getCurrentTicketAdmissionsByLegIndex(trip, legIndex);

  if (!admissions.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2" data-testid="current-ticket-section">
      <Typography variant="body1-bold">
        <TransText i18nKey="currentTicket" />
      </Typography>
      <div className="flex flex-col gap-2 rounded-md border border-neutral-light p-3">
        {admissions.map((admission, index) => (
          <div
            key={admission.id ?? index}
            className="grid grid-cols-[20px,1fr,auto] items-center gap-x-2"
          >
            <Icons.ticket
              width={20}
              height={20}
              className="shrink-0 text-primary"
            />
            <Typography variant="body2-bold" className="min-w-0 truncate">
              {getAdmissionTitle(admission)}
            </Typography>
            <Typography variant="body2-bold" className="shrink-0 text-primary">
              <PriceDisplay amount={admission.price?.amount ?? 0} />
            </Typography>
            <Typography
              variant="body2"
              className="col-start-2 self-start text-neutral"
            >
              <TransText
                i18nKey="passengersCount"
                values={{ count: admission.passengerIds?.length }}
              />
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CurrentTicketSection;

const getAdmissionTitle = (admission: AdmissionDTO): string =>
  admission.productDescription ??
  admission.productSummary ??
  admission.summary ??
  '';

const getActiveAdmissions = (trip: BookedTripDTO | null): Array<AdmissionDTO> =>
  trip?.bookedOffers
    ?.flatMap(({ admissions }) => admissions ?? [])
    .filter((admission) => !isAdmissionInactive(admission)) ?? [];

const getCurrentTicketAdmissionsByLegIndex = (
  trip: BookedTripDTO | null,
  legIndex: number
): Array<AdmissionDTO> => {
  const admissions = getActiveAdmissions(trip);
  const originalLegId = trip?.legs?.[legIndex]?.id;

  if (!originalLegId) {
    return admissions;
  }

  const matchingAdmissions = admissions.filter((admission) =>
    admission.coveredLegIds?.includes(originalLegId)
  );

  return matchingAdmissions.length ? matchingAdmissions : admissions;
};
